import { useMemo } from 'react';
import * as R from 'remeda';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue, QueueListQuery } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import { useUnpaginatedQueues } from '../../../business/queues/useUnpaginatedQueues';
import { useUnpaginatedWorkspaces } from '../../../business/workspaces/useUnpaginatedWorkspaces';
import { Prettify } from '../../../lib/typeUtils';

type UseWorkspacesWithQueuesProps = {
  enableQueries: boolean;
  onWorkspacesSuccessfulFetched?: (workspaces: Workspace[]) => void;
  refetchInterval?: number;
  queueFilters?: QueueListQuery;
};

export type WorkspaceWithQueues = Prettify<
  Omit<Workspace, 'queues' | 'metadata'> & {
    queues: Queue[];
    toReviewCount: number;
  }
>;

export const useWorkspacesWithQueues = ({
  enableQueries,
  onWorkspacesSuccessfulFetched,
  refetchInterval,
  queueFilters,
}: UseWorkspacesWithQueuesProps) => {
  const { data: workspaces, isFetching: isWorkspacesFetching } =
    useUnpaginatedWorkspaces(undefined, {
      enabled: enableQueries,
      keepPreviousData: true,
      onSuccess: onWorkspacesSuccessfulFetched,
      refetchInterval,
    });

  const {
    data: queues,
    isLoading: isQueuesLoading,
    isFetching: isQueuesFetching,
  } = useUnpaginatedQueues(queueFilters ?? {}, {
    enabled: enableQueries,
    keepPreviousData: true,
    refetchInterval,
  });

  const queueObject = useMemo(() => {
    return queues?.reduce<Record<number, Queue>>((acc, queue) => {
      return {
        ...acc,
        [queue.id]: queue,
      };
    }, {});
  }, [queues]);

  const allWorkspacesWithQueues = useMemo(
    () =>
      workspaces
        ?.map(workspace => {
          const { queues: queueUrls, metadata, ...rest } = workspace;
          const queuesOfWorkspace = R.pipe(
            queueUrls?.map(queueUrl => {
              const queueId = getIDFromUrl(queueUrl);
              return queueObject?.[queueId] || undefined;
            }),
            R.filter(R.isTruthy),
            R.sortBy(({ name }) => name)
          );

          const toReviewCount = queuesOfWorkspace.reduce<number>(
            (acc, { counts: { to_review, reviewing, importing } }) =>
              R.sum([acc, to_review ?? 0, reviewing ?? 0, importing ?? 0]),
            0
          );

          return {
            ...rest,
            queues: queuesOfWorkspace,
            toReviewCount,
          };
        })
        ?.sort((a, b) => a.name.localeCompare(b.name)),

    [queueObject, workspaces]
  );

  const workspacesWithQueues = useMemo(() => {
    return allWorkspacesWithQueues?.filter(
      workspace => workspace.queues.length && workspace.queues[0]
    );
  }, [allWorkspacesWithQueues]);

  return {
    workspacesWithQueues,
    allWorkspacesWithQueues,
    queues,
    workspaces,
    isLoading: isQueuesLoading,
    isWorkspacesFetching,
    isQueuesFetching,
  };
};
